import React, { useEffect, useState, lazy, createElement } from 'react';
import { CheckCircle2, XCircle, Star, FileText, Zap, ShieldCheck, ChevronDown, MessageSquare, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}
// --- Sub-components ---
const SessionCountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState({
    minutes: 30,
    seconds: 0
  });
  useEffect(() => {
    const STORAGE_KEY = 'megapack_countdown_end';
    const DURATION = 30 * 60 * 1000; // 30 minutes
    const getEndTime = () => {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const endTime = parseInt(stored, 10);
        if (endTime > Date.now()) return endTime;
      }
      const newEndTime = Date.now() + DURATION;
      localStorage.setItem(STORAGE_KEY, newEndTime.toString());
      return newEndTime;
    };
    const endTime = getEndTime();
    const timer = setInterval(() => {
      const now = Date.now();
      const diff = Math.max(0, endTime - now);
      if (diff === 0) {
        // Reset if it reaches zero
        const newEndTime = Date.now() + DURATION;
        localStorage.setItem(STORAGE_KEY, newEndTime.toString());
        return;
      }
      const minutes = Math.floor(diff % (1000 * 60 * 60) / (1000 * 60));
      const seconds = Math.floor(diff % (1000 * 60) / 1000);
      setTimeLeft({
        minutes,
        seconds
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  return <div className="flex flex-col items-center w-full max-w-md mx-auto" data-id="element-1">
      <p className="text-white font-bold uppercase tracking-widest text-[10px] md:text-xs mb-1 animate-pulse" data-id="element-2">
        ⏳ OFERTA ESPECIAL TERMINA EN:
      </p>
      <div className="flex gap-2 font-mono text-3xl md:text-5xl font-black text-white" data-id="element-3">
        <div className="bg-[#D32F2F] px-3 py-2 rounded-xl border-2 border-white/30 shadow-[0_0_20px_rgba(211,47,47,0.4)] min-w-[70px] md:min-w-[90px] text-center" data-id="element-4">
          {String(timeLeft.minutes).padStart(2, '0')}
          <span className="block text-[8px] md:text-[10px] uppercase font-sans font-bold tracking-tighter mt-0.5 opacity-80" data-id="element-5">
            Minutos
          </span>
        </div>
        <span className="text-white self-center -mt-4" data-id="element-6">:</span>
        <div className="bg-[#D32F2F] px-3 py-2 rounded-xl border-2 border-white/30 shadow-[0_0_20px_rgba(211,47,47,0.4)] min-w-[70px] md:min-w-[90px] text-center" data-id="element-7">
          {String(timeLeft.seconds).padStart(2, '0')}
          <span className="block text-[8px] md:text-[10px] uppercase font-sans font-bold tracking-tighter mt-0.5 opacity-80" data-id="element-8">
            Segundos
          </span>
        </div>
      </div>
    </div>;
};
const FloatingNotification = () => {
  const [visible, setVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAllowedSection, setIsAllowedSection] = useState(true);
  const notifications = [{
    n: 'Marina P.',
    l: 'Buenos Aires',
    t: 'hace 6 minutos',
    p: 'el Mega Pack 🚨'
  }, {
    n: 'Carlos R.',
    l: 'Córdoba',
    t: 'hace 2 minutos',
    p: 'el Pack VIP 💎'
  }, {
    n: 'Sofía F.',
    l: 'Rosario',
    t: 'hace 15 minutos',
    p: 'el Mega Pack 🚨'
  }, {
    n: 'Javier M.',
    l: 'Mendoza',
    t: 'hace 8 minutos',
    p: 'el Pack VIP 💎'
  }];
  useEffect(() => {
    const handleScroll = () => {
      const pricingSection = document.getElementById('pricing');
      const finalCtaSection = document.getElementById('final-cta');
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      let allowed = true;
      // Check Pricing Section
      if (pricingSection) {
        const rect = pricingSection.getBoundingClientRect();
        // If pricing section is in viewport
        if (rect.top < windowHeight && rect.bottom > 0) {
          allowed = false;
        }
      }
      // Check Final CTA (last 3 scrolls)
      if (finalCtaSection) {
        const rect = finalCtaSection.getBoundingClientRect();
        // 3 scrolls = 3 * windowHeight
        if (rect.top < windowHeight * 3) {
          allowed = false;
        }
      }
      setIsAllowedSection(allowed);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    // Initial 3s delay
    const initialTimeout = setTimeout(() => {
      if (isAllowedSection) setVisible(true);
    }, 3000);
    // Cycle: 4s visible, 1s hidden (total 5s per notification)
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % notifications.length);
        if (isAllowedSection) setVisible(true);
      }, 1000);
    }, 5000);
    // Auto-close after 4s if it's visible
    const autoCloseInterval = setInterval(() => {
      setVisible(false);
    }, 5000); // Every 5s, we ensure it's closed (4s visible + 1s hidden)
    // Actually, the interval above handles the hiding.
    // Let's refine the logic to be cleaner.
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
      clearInterval(autoCloseInterval);
    };
  }, [isAllowedSection]);
  // Refined timing logic:
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let cycleInterval: NodeJS.Timeout;
    const startCycle = () => {
      cycleInterval = setInterval(() => {
        // Show
        if (isAllowedSection) {
          setVisible(true);
          // Auto-hide after 4s
          timeoutId = setTimeout(() => {
            setVisible(false);
            setCurrentIndex(prev => (prev + 1) % notifications.length);
          }, 4000);
        } else {
          // If not allowed, just move to next index after "cycle" time
          setCurrentIndex(prev => (prev + 1) % notifications.length);
        }
      }, 5000); // 4s visible + 1s gap
    };
    // Initial 3s delay
    const initialDelay = setTimeout(() => {
      if (isAllowedSection) {
        setVisible(true);
        timeoutId = setTimeout(() => {
          setVisible(false);
          setCurrentIndex(prev => (prev + 1) % notifications.length);
          startCycle();
        }, 4000);
      } else {
        startCycle();
      }
    }, 3000);
    return () => {
      clearTimeout(initialDelay);
      clearTimeout(timeoutId);
      clearInterval(cycleInterval);
    };
  }, [isAllowedSection]);
  const current = notifications[currentIndex];
  return <AnimatePresence data-id="element-9">
      {visible && <motion.div initial={{
      opacity: 0,
      y: 50,
      x: -20
    }} animate={{
      opacity: 1,
      y: 0,
      x: 0
    }} exit={{
      opacity: 0,
      y: 20
    }} className="fixed bottom-6 left-6 z-50 bg-white shadow-2xl rounded-lg p-4 border border-slate-100 flex items-center gap-4 max-w-sm" data-id="element-10">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-[#10A37F]" data-id="element-11">
            <Check size={20} data-id="element-12" />
          </div>
          <div data-id="element-13">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-14">
              Compra verificada
            </p>
            <p className="text-sm text-slate-800" data-id="element-15">
              <span className="font-bold" data-id="element-16">{current.n}</span> de {current.l}
              <br data-id="element-17" />
              compró {current.p}
            </p>
            <p className="text-xs text-slate-400 mt-1" data-id="element-18">{current.t}</p>
          </div>
          <button onClick={() => setVisible(false)} className="text-slate-300 hover:text-slate-500" data-id="element-19">
            <XCircle size={16} data-id="element-20" />
          </button>
        </motion.div>}
    </AnimatePresence>;
};
const SectionTitle = ({
  children,
  subtitle,
  light = false
}: {
  children: React.ReactNode;
  subtitle?: string;
  light?: boolean;
}) => <div className="text-center mb-4 md:mb-8" data-id="element-21">
    <h2 className={`text-3xl md:text-5xl font-bold mb-2 md:mb-3 ${light ? 'text-white' : 'text-slate-900'}`} data-id="element-22">
      {children}
    </h2>
    {subtitle && <p className={`text-base md:text-lg ${light ? 'text-slate-200' : 'text-slate-600'} max-w-2xl mx-auto font-sans px-4`} data-id="element-23">
        {subtitle}
      </p>}
  </div>;
const CTAButton = ({
  children,
  secondary = false,
  className = '',
  href = '',
  onClick
}: {
  children: React.ReactNode;
  secondary?: boolean;
  className?: string;
  href?: string;
  onClick?: () => void;
}) => {
  const handleClick = (e: React.MouseEvent) => {
    if (window.fbq) {
      if (href.includes('cart')) {
        const isTier2 = href.includes('44902683213893');
        window.fbq('track', 'InitiateCheckout', {
          content_name: isTier2 ? 'Pack VIP' : 'Pack Completo',
          value: isTier2 ? 24990 : 19990,
          currency: 'ARS'
        });
      } else if (href === '#pricing') {
        window.fbq('track', 'ViewContent', {
          content_name: 'Pricing Section'
        });
      }
    }
    if (onClick) onClick();
  };
  const content = <button className={`
        w-full md:w-auto px-10 py-5 rounded-full font-bold text-lg transition-all transform hover:scale-105 active:scale-95 shadow-xl animate-cta-pulse
        ${secondary ? 'bg-white text-[#1A237E] border-2 border-slate-200 hover:bg-slate-50' : 'bg-[#2E7D32] text-white hover:bg-[#1B5E20]'}
        ${className}
      `} onClick={handleClick} data-id="element-24">
      {children}
    </button>;
  if (href) {
    const isInternal = href.startsWith('#');
    return <a href={href} target={isInternal ? undefined : '_blank'} rel={isInternal ? undefined : 'noopener noreferrer'} className="w-full md:w-auto block" onClick={e => {
      handleClick(e);
      if (isInternal) {
        e.preventDefault();
        const element = document.querySelector(href);
        if (element) element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }} data-id="element-25">
        {content}
      </a>;
  }
  return content;
};
// --- Main Page ---
export function LandingPage() {
  useEffect(() => {
    // Defer Facebook Pixel load by 3 seconds for better FCP
    const fbTimeout = setTimeout(() => {
      if (!window.fbq) {
        const f = window as any;
        const b = document;
        const e = 'script';
        const v = 'https://connect.facebook.net/en_US/fbevents.js';
        let n: any, t: any, s: any;
        f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        n = f.fbq;
        if (!f._fbq) f._fbq = n;
        f.fbq.push = f.fbq;
        f.fbq.loaded = !0;
        f.fbq.version = '2.0';
        f.fbq.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        if (s && s.parentNode) {
          s.parentNode.insertBefore(t, s);
        } else {
          b.head.appendChild(t);
        }
      }
      if (window.fbq) {
        window.fbq('init', '1313291250496067');
        window.fbq('track', 'PageView');
      }
    }, 3000);
    return () => clearTimeout(fbTimeout);
  }, []);
  return <div className="min-h-screen font-sans" data-id="element-26">
      <FloatingNotification data-id="element-27" />

      {/* Meta Pixel Noscript */}
      <noscript data-id="element-28">
        <img height="1" width="1" style={{
        display: 'none'
      }} src="https://www.facebook.com/tr?id=1313291250496067&ev=PageView&noscript=1" data-id="element-29" />
      </noscript>

      {/* Hero Section */}
      <header className="relative bg-[#1A237E] min-h-[100svh] flex flex-col text-white overflow-hidden" data-id="element-30">
        <div className="absolute top-0 left-0 w-full h-1 flex" data-id="element-31">
          <div className="flex-1 bg-white/20" data-id="element-32"></div>
          <div className="flex-1 bg-white/40" data-id="element-33"></div>
          <div className="flex-1 bg-white/20" data-id="element-34"></div>
        </div>

        <div className="container mx-auto px-4 flex-grow flex flex-col justify-between pt-2 pb-4 md:py-16 relative z-10" data-id="element-35">
          <div className="max-w-4xl mx-auto text-center flex flex-col flex-grow justify-between py-1 md:py-2" data-id="element-36">
            <div className="inline-flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full text-white text-[10px] md:text-sm font-bold mb-1 md:mb-8 backdrop-blur-sm self-center animate-fade-in" data-id="element-37">
              🚨 MEGA PACK NEGOCIO VISIBLE™
            </div>

            <h1 className="font-bold mb-1 md:mb-8 text-white md:text-[48px] text-[24px] leading-tight" data-id="element-38">
              ¿Tu negocio es bueno pero nadie te ve o conoce?
            </h1>

            <div className="text-white/90 mb-3 md:mb-12 max-w-3xl mx-auto px-2 md:text-[18px] text-[13px] space-y-1.5 md:space-y-4 font-medium leading-tight md:leading-relaxed" data-id="element-39">
              <p className="text-[18px] md:text-[32px] font-bold text-white" data-id="element-40">
                La competencia se lleva tus clientes.
              </p>
              <p data-id="element-41">
                Hoy buscan en redes sociales antes de comprar o pedir un turno.
              </p>
              <p className="font-bold" data-id="element-42">Si no aparecés ahí, no existís.</p>
              <div className="bg-white/5 py-2 px-4 rounded-xl border border-white/10 backdrop-blur-sm" data-id="element-43">
                <p className="mb-1 text-[#2E7D32] font-bold uppercase tracking-wider text-[11px] md:text-sm" data-id="element-44">
                  El Mega Pack te enseña exactamente cómo:
                </p>
                <ul className="text-left max-w-[280px] md:max-w-md mx-auto space-y-0.5 md:space-y-2 text-[12px] md:text-base" data-id="element-45">
                  <li className="flex items-center gap-2" data-id="element-46">
                    → <strong data-id="element-47">Aparecer</strong> en el celular de tus clientes
                  </li>
                  <li className="flex items-center gap-2" data-id="element-48">
                    → <strong data-id="element-49">Atraer</strong> nuevas personas cada semana
                  </li>
                  <li className="flex items-center gap-2" data-id="element-50">
                    → <strong data-id="element-51">Vender</strong> más sin gastar más
                  </li>
                </ul>
              </div>
              <p className="pt-1 font-bold text-white text-[14px] md:text-lg" data-id="element-52">
                5 Tomos + 3 Bonos. Hoy mismo.
              </p>
            </div>

            <div className="relative max-w-[320px] md:max-w-5xl mx-auto mb-3 md:mb-16 group animate-fade-in-up" data-id="element-53">
              {/* Dynamic Background Glows */}
              <div className="absolute inset-0 bg-[#2E7D32] hidden md:block blur-[100px] opacity-20 group-hover:opacity-30 transition-opacity duration-700" data-id="element-54"></div>
              <div className="absolute -inset-4 bg-[#4CAF50] hidden md:block blur-[80px] opacity-10 group-hover:opacity-25 transition-opacity duration-1000 animate-pulse" data-id="element-55"></div>

              <div className="relative z-10 rounded-2xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.5)] border border-white/10" data-id="element-56">
                {/* Visual context overlay for "Dynamic Background" feel */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A237E]/40 to-transparent pointer-events-none z-20" data-id="element-57"></div>

                <img src="https://cdn.magicpatterns.com/uploads/uHkXDTuEvEFVvC9EJ68kcs/megapack_negocio_visible_opt_1200.webp" alt="Mega Pack Negocio Visible Mockup" className="w-full h-auto transform group-hover:scale-[1.02] transition-transform duration-700" width={1400} height={900} decoding="async"
              // @ts-ignore
              fetchpriority="high" data-id="element-58" />

                {/* Floating "Social Visibility" indicators */}
                <div className="absolute top-4 right-4 md:top-10 md:right-10 z-30 animate-bounce delay-700" data-id="element-59">
                  <div className="bg-[#4CAF50] text-white p-2 md:p-3 rounded-full shadow-lg border-2 border-white/20" data-id="element-60">
                    <Zap size={20} className="md:w-6 md:h-6" data-id="element-61" />
                  </div>
                </div>
              </div>

              {/* Green Glow for Visibility */}
              <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-3/4 h-20 bg-[#4CAF50] blur-[60px] opacity-20 pointer-events-none" data-id="element-62"></div>
            </div>

            <div className="flex flex-col items-center gap-2 md:gap-6 mb-2 md:mb-12" data-id="element-63">
              <div className="w-full mb-4" data-id="element-64">
                <SessionCountdownTimer data-id="element-65" />
              </div>
              <CTAButton href="#pricing" className="uppercase tracking-wide px-6 py-3 md:px-12 md:py-5 text-xs md:text-lg" data-id="element-66">
                → QUIERO MI MEGA PACK AHORA ⚡
              </CTAButton>

              <div className="flex flex-wrap justify-center gap-1.5 md:gap-4 text-[#1A237E] font-medium text-[8px] md:text-sm" data-id="element-67">
                <div className="flex items-center gap-1 bg-white/90 px-2 py-0.5 rounded-full" data-id="element-68">
                  <FileText size={10} data-id="element-69" /> 100% PDF
                </div>
                <div className="flex items-center gap-1 bg-white/90 px-2 py-0.5 rounded-full" data-id="element-70">
                  <Zap size={10} data-id="element-71" /> Acceso en 5 min
                </div>
                <div className="flex items-center gap-1 bg-white/90 px-2 py-0.5 rounded-full" data-id="element-72">
                  <ShieldCheck size={10} data-id="element-73" /> Compra protegida
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Section */}
      <section className="py-8 md:py-12 bg-[#1A237E] border-b border-white/10" data-id="element-74">
        <div className="container mx-auto px-4" data-id="element-75">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto text-center" data-id="element-76">
            {[{
            label: 'emprendedores',
            value: '+1.200'
          }, {
            label: 'tomos y bonos',
            value: '8'
          }, {
            label: 'páginas listas',
            value: '350+'
          }, {
            label: 'calificación',
            value: '4.9/5',
            stars: true
          }].map((stat, i) => <div key={i} data-id="element-77">
                <p className="text-3xl md:text-4xl font-bold text-[#2E7D32] mb-1" data-id="element-78">
                  {stat.value}
                </p>
                {stat.stars && <div className="flex justify-center text-yellow-500 mb-1" data-id="element-79">
                    {[...Array(5)].map((_, j) => <Star key={j} size={14} fill="currentColor" data-id="element-80" />)}
                  </div>}
                <p className="text-[10px] md:text-xs uppercase tracking-widest text-white font-bold" data-id="element-81">
                  {stat.label}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Testimonials 1 */}
      <section className="py-6 md:py-12 bg-white" data-id="element-82">
        <div className="container mx-auto px-4" data-id="element-83">
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-84">
            {[{
            n: 'Marina P.',
            l: 'Buenos Aires · Dueña de local de ropa',
            q: 'Tenía un local de ropa y dependía de los clientes de siempre. En 3 semanas de aplicar el sistema me entraron 40 consultas nuevas por Instagram. Nunca me había pasado.',
            i: 'MP'
          }, {
            n: 'Carlos R.',
            l: 'Córdoba · Electricista independiente',
            q: 'El Tomo 3 me cambió la vida. Con $5 USD por día empecé a tener consultas todos los días por WhatsApp. Antes no sabía ni que Meta Ads existía.',
            i: 'CR'
          }, {
            n: 'Sofía F.',
            l: 'Rosario · Esteticista',
            q: 'Por fin un material en argentino, con ejemplos reales de negocios como el mío. No de empresas de Estados Unidos.',
            i: 'SF'
          }].map((test, i) => <div key={i} className="bg-[#F5F5F5] p-8 rounded-2xl shadow-sm border border-slate-100" data-id="element-85">
                <div className="flex gap-1 mb-4 text-yellow-500" data-id="element-86">
                  {[...Array(5)].map((_, j) => <Star key={j} size={16} fill="currentColor" data-id="element-87" />)}
                </div>
                <p className="text-[#37474F] italic mb-6 leading-relaxed" data-id="element-88">
                  "{test.q}"
                </p>
                <div className="flex items-center gap-4" data-id="element-89">
                  <div className="w-10 h-10 bg-[#1A237E] rounded-full flex items-center justify-center font-bold text-white" data-id="element-90">
                    {test.i}
                  </div>
                  <div data-id="element-91">
                    <p className="font-bold text-[#1A237E]" data-id="element-92">{test.n}</p>
                    <p className="text-xs text-[#37474F]" data-id="element-93">{test.l}</p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-6 md:py-12 bg-[#ECEFF1]" data-id="element-94">
        <div className="container mx-auto px-4" data-id="element-95">
          <SectionTitle subtitle="Tu negocio es bueno. Lo sabés vos y lo saben los que ya te conocen. El problema es que nadie más te encuentra. O tus anuncios gastan plata y no traen resultados. O no sabés qué publicar en redes y terminás no publicando nada. Y mientras tanto la competencia que hace peor trabajo que vos aparece primero en Instagram y se lleva a tus clientes. No es falta de esfuerzo. Es falta de sistema." data-id="element-96">
            <span className="text-[#1A237E]" data-id="element-97">
              ¿Cuántos clientes más vas a perder por no tener visibilidad
              digital?
            </span>
          </SectionTitle>

          <div className="max-w-3xl mx-auto text-center mb-6 md:mb-10" data-id="element-98">
            <p className="text-xl font-bold text-[#2E7D32]" data-id="element-99">
              No es falta de esfuerzo. Es falta de sistema.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto" data-id="element-100">
            {['❌ Sin saber qué publicar ni cuándo', '❌ Sin entender cómo funciona Meta Ads', '❌ Sin un sistema para cerrar ventas por WhatsApp', '❌ Sin saber cómo convertir seguidores en clientes', '❌ Con miedo a gastar en publicidad y tirar la plata', '❌ Sin herramientas para medir si lo que hacés funciona', '❌ Sabiendo que necesitás crecer y sin saber por dónde empezar'].map((item, i) => <div key={i} className="bg-white p-4 rounded-lg flex gap-3 items-start" data-id="element-101">
                <XCircle className="text-[#C62828] shrink-0 mt-1" size={20} data-id="element-102" />
                <p className="text-[#37474F] font-medium" data-id="element-103">{item}</p>
              </div>)}
          </div>

          <div className="mt-8 max-w-3xl mx-auto bg-[#FFF8E1] border-l-8 border-[#2E7D32] p-8 rounded-r-2xl shadow-sm text-center" data-id="element-104">
            <p className="text-2xl font-bold text-[#1A237E] italic" data-id="element-105">
              ¿Y si esta fuera la última semana que perdés clientes por no tener
              el sistema correcto?
            </p>
          </div>
        </div>
      </section>

      {/* Product Hero */}
      <section className="py-6 md:py-12 bg-[#1A237E] text-white" data-id="element-106">
        <div className="container mx-auto px-4 text-center" data-id="element-107">
          <div className="inline-block bg-[#2E7D32] text-white px-6 py-2 rounded-full font-bold text-sm uppercase tracking-widest mb-6" data-id="element-108">
            SOLUCIÓN INTEGRAL
          </div>
          <h2 className="text-3xl md:text-6xl font-bold mb-4 text-white" data-id="element-109">
            🔥 MEGA PACK NEGOCIO VISIBLE™ PACK COMPLETO
          </h2>
          <p className="text-lg md:text-2xl text-white/70 mb-8 max-w-3xl mx-auto" data-id="element-110">
            5 Tomos + 3 Bonos · 350+ páginas · Todo lo que necesitás para atraer
            clientes, vender más y controlar tu negocio.
          </p>

          <div className="max-w-4xl mx-auto mb-8 relative group" data-id="element-111">
            <div className="absolute inset-0 bg-[#2E7D32] hidden md:block blur-3xl opacity-10 group-hover:opacity-20 transition-opacity duration-700" data-id="element-112"></div>
            <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl border-2 border-white/10" data-id="element-113">
              <img src="https://cdn.magicpatterns.com/uploads/uHkXDTuEvEFVvC9EJ68kcs/megapack_negocio_visible_opt_1200.webp" alt="Mega Pack Negocio Visible Mockup" className="w-full h-auto" loading="lazy" decoding="async" width={1400} height={900} data-id="element-114" />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#1A237E]/30 pointer-events-none" data-id="element-115"></div>
            </div>
          </div>

          <div className="max-w-3xl mx-auto bg-[#283593] border-l-8 border-[#2E7D32] p-6 rounded-r-xl text-left" data-id="element-116">
            <p className="text-lg md:text-xl font-bold text-white mb-2" data-id="element-117">
              Desarrollado para negocios físicos y emprendedores de Argentina.
            </p>
            <p className="text-white/80" data-id="element-118">
              100% adaptado a la realidad del mercado local argentino.
            </p>
          </div>
        </div>
      </section>

      {/* The 5 Modules */}
      <section className="py-6 md:py-12 bg-white" data-id="element-119">
        <div className="container mx-auto px-4" data-id="element-120">
          <SectionTitle subtitle="Cada tomo incluye estrategias, plantillas y pasos listos para aplicar." data-id="element-121">
            <span className="text-[#1A237E]" data-id="element-122">📚 5 TOMOS COMPLETOS</span>
          </SectionTitle>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto" data-id="element-123">
            {[{
            t: 'TOMO 1 — PRESENCIA DIGITAL DESDE CERO',
            bg: 'bg-[#1A237E]',
            d: 'Cómo crear y optimizar tu perfil en redes sociales para que los clientes de tu zona te encuentren.',
            f: ['Cómo configurar tu perfil de Instagram y Facebook', 'La bio perfecta para negocios locales', 'Cómo aparecer en búsquedas locales', 'Google Maps y reseñas digitales', 'Los primeros 30 días de estrategia']
          }, {
            t: 'TOMO 2 — CONTENIDO QUE ATRAE CLIENTES',
            bg: 'bg-[#0D47A1]',
            d: 'Qué publicar, cuándo y cómo para conseguir clientes sin pagar publicidad.',
            f: ['El calendario de contenido mensual', 'Los 5 tipos de post que más venden', 'Cómo usar la IA para crear contenido', 'Estrategia de hashtags locales', 'Plantillas de contenido por rubro']
          }, {
            t: 'TOMO 3 — META ADS PARA NEGOCIOS LOCALES',
            bg: 'bg-[#283593]',
            d: 'Cómo conseguir clientes nuevos con solo $5 USD por día sin ser experto en publicidad.',
            f: ['Cómo crear tu primer anuncio', 'Segmentación para llegar a personas de tu barrio', 'Cómo leer los resultados', 'El anuncio que más convierte para negocios locales', 'Cómo escalar cuando funciona']
          }, {
            t: 'TOMO 4 — TIKTOK Y REELS PARA NEGOCIOS LOCALES',
            bg: 'bg-[#1A237E]',
            d: 'Cómo volverte viral en tu barrio y atraer clientes con videos simples desde el celular.',
            f: ['Las 5 fórmulas de videos que venden', 'Edición con CapCut paso a paso', 'Cómo usar el algoritmo a tu favor', 'De 0 a 10.000 seguidores locales', 'Cómo convertir vistas en clientes']
          }, {
            t: 'TOMO 5 — EL EMBUDO DE CLIENTES',
            bg: 'bg-[#0D47A1]',
            d: 'Cómo convertir seguidores en clientes que pagan, vuelven y te recomiendan.',
            f: ['Las 4 etapas del embudo local', 'WhatsApp Business configurado', 'Scripts de cierre de ventas', 'Sistema de fidelización', 'Cómo generar referidos automáticos']
          }].map((tomo, i) => <div key={i} className={`p-8 rounded-2xl ${tomo.bg} shadow-xl hover:scale-[1.02] transition-all`} data-id="element-124">
                <h3 className="text-xl font-bold mb-2 text-white" data-id="element-125">{tomo.t}</h3>
                <p className="text-[#2E7D32] font-bold mb-4 text-sm uppercase tracking-wider" data-id="element-126">
                  Contenido Exclusivo
                </p>
                <p className="text-white/80 mb-6 text-sm leading-relaxed" data-id="element-127">
                  {tomo.d}
                </p>
                <ul className="space-y-3" data-id="element-128">
                  {tomo.f.map((feature, j) => <li key={j} className="flex items-center gap-3 text-sm text-white" data-id="element-129">
                      <CheckCircle2 size={16} className="text-white shrink-0" data-id="element-130" />{' '}
                      {feature}
                    </li>)}
                </ul>
              </div>)}
          </div>
        </div>
      </section>

      {/* Bonuses */}
      <section className="py-6 md:py-12 bg-[#ECEFF1]" data-id="element-131">
        <div className="container mx-auto px-4" data-id="element-132">
          <SectionTitle subtitle="Valor total bonos: $24.000 → GRATIS con tu pack" data-id="element-133">
            <span className="text-[#1A237E]" data-id="element-134">🎁 3 BONOS ESTRATÉGICOS</span>
          </SectionTitle>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-135">
            {[{
            n: '01',
            t: '100 POSTS LISTOS PARA COPIAR',
            d: 'Publicaciones listas para usar en Facebook, Instagram y TikTok para cualquier negocio local. Solo reemplazás el nombre de tu negocio y publicás.',
            v: '$8.000'
          }, {
            n: '02',
            t: 'GUÍA DE PRECIOS Y PROMOCIONES',
            d: 'Cómo fijar precios que venden, crear promociones que funcionan y ganar más sin bajar la calidad.',
            v: '$8.000'
          }, {
            n: '03',
            t: 'SCRIPTS DE VENTA POR WHATSAPP',
            d: 'Conversaciones probadas para cerrar más ventas, responder objeciones y convertir consultas en clientes reales.',
            v: '$8.000'
          }].map((bono, i) => <div key={i} className="bg-white p-8 rounded-2xl shadow-sm border-t-8 border-[#2E7D32] relative overflow-hidden" data-id="element-136">
                <div className="absolute top-0 right-0 bg-[#2E7D32] text-white text-[10px] font-bold px-4 py-1 rounded-bl-lg uppercase" data-id="element-137">
                  Gratis
                </div>
                <span className="text-[#1A237E] font-bold text-sm mb-2 block" data-id="element-138">
                  BONO {bono.n}
                </span>
                <h4 className="text-xl font-bold mb-3 text-[#1A237E]" data-id="element-139">
                  {bono.t}
                </h4>
                <p className="text-[#37474F] text-sm mb-6 leading-relaxed" data-id="element-140">
                  {bono.d}
                </p>
                <p className="text-sm line-through text-[#9E9E9E]" data-id="element-141">{bono.v}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-4 md:py-14 bg-[#1A237E] text-white flex flex-col justify-center" id="pricing" data-id="element-142">
        <div className="container mx-auto px-2" data-id="element-143">
          <div className="text-center mb-6 md:mb-12" data-id="element-144">
            <h2 className="text-2xl md:text-5xl font-bold text-white mb-4" data-id="element-145">
              LLEVATE TU PACK
            </h2>
            <SessionCountdownTimer data-id="element-146" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-8 max-w-5xl mx-auto h-full" data-id="element-147">
            {/* Pack Completo */}
            <div className="bg-[#0D1B2A] rounded-2xl p-3 md:p-12 text-white shadow-2xl flex flex-col border-2 md:border-4 border-[#2E7D32] relative" data-id="element-148">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#2E7D32] text-white font-bold px-4 py-0.5 rounded-full text-[8px] md:text-xs uppercase tracking-widest" data-id="element-149">
                PACK COMPLETO ⭐
              </div>
              <div className="mb-2 md:mb-4" data-id="element-150">
                <h3 className="text-sm md:text-2xl font-bold text-white leading-tight" data-id="element-151">
                  MEGA PACK NEGOCIO VISIBLE
                </h3>
                <p className="text-[10px] md:text-sm text-white/70 mt-1" data-id="element-152">
                  Todo lo que necesitas para atraer clientes y vender más desde
                  tu celular.
                </p>
              </div>
              <div className="mb-2 md:mb-8 flex items-baseline gap-2" data-id="element-153">
                <p className="text-[#9E9E9E] line-through text-[10px] md:text-lg" data-id="element-154">
                  $48.000
                </p>
                <p className="text-xl md:text-6xl font-bold text-[#2E7D32]" data-id="element-155">
                  $19.990{' '}
                  <span className="text-[10px] md:text-xl font-medium" data-id="element-156">
                    ARS
                  </span>
                </p>
              </div>
              <ul className="space-y-1 md:space-y-4 mb-6 md:mb-10 flex-grow" data-id="element-157">
                {['✅ 5 Tomos (350+ páginas)', '✅ 3 Bonos ($24.000 valor)', '✅ Acceso inmediato', '✅ Garantía 7 días'].map((item, i) => <li key={i} className="flex items-start gap-1.5 text-white text-[10px] md:text-sm font-bold" data-id="element-158">
                    <CheckCircle2 size={10} className="text-[#2E7D32] shrink-0 mt-0.5" data-id="element-159" />
                    <span data-id="element-160">{item}</span>
                  </li>)}
              </ul>
              <CTAButton href="https://checkout.guiadigital360.online/cart/44902679969861:1" className="w-full bg-[#2E7D32] text-white text-xs md:text-2xl py-2.5 md:py-6" data-id="element-161">
                → QUIERO ESTE PACK ⚡
              </CTAButton>
            </div>

            {/* Pack VIP */}
            <div className="bg-[#0D1B2A] rounded-2xl p-3 md:p-12 text-white shadow-2xl flex flex-col border-2 md:border-4 border-[#2E7D32] relative" data-id="element-162">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#2E7D32] text-white font-bold px-4 py-0.5 rounded-full text-[8px] md:text-xs uppercase tracking-widest" data-id="element-163">
                🔥 MÁS COMPLETO
              </div>
              <div className="mb-2 md:mb-4" data-id="element-164">
                <h3 className="text-sm md:text-2xl font-bold text-white leading-tight" data-id="element-165">
                  MEGA PACK VIP + MASTER FINANZAS
                </h3>
                <p className="text-[10px] md:text-sm text-white/70 mt-1" data-id="element-166">
                  El pack completo + Master Class exclusiva de gestión
                  financiera y rentabilidad.
                </p>
              </div>
              <div className="mb-2 md:mb-8 flex items-baseline gap-2" data-id="element-167">
                <p className="text-[#9E9E9E] line-through text-[10px] md:text-lg" data-id="element-168">
                  $72.000
                </p>
                <p className="text-xl md:text-6xl font-bold text-[#2E7D32]" data-id="element-169">
                  $24.990{' '}
                  <span className="text-[10px] md:text-xl font-medium" data-id="element-170">
                    ARS
                  </span>
                </p>
              </div>
              <ul className="space-y-1 md:space-y-4 mb-6 md:mb-10 flex-grow" data-id="element-171">
                {['✅ 5 Tomos (350+ páginas)', '✅ 3 Bonos ($24.000 valor)', '✅ Master-Finanzas (módulo exclusivo)', '✅ Acceso inmediato', '✅ Garantía 7 días', '✅ Soporte prioritario'].map((item, i) => <li key={i} className="flex items-start gap-1.5 text-white text-[10px] md:text-sm font-bold" data-id="element-172">
                    <CheckCircle2 size={10} className="text-[#2E7D32] shrink-0 mt-0.5" data-id="element-173" />
                    <span data-id="element-174">{item}</span>
                  </li>)}
              </ul>
              <CTAButton href="https://checkout.guiadigital360.online/cart/44902683213893:1" className="w-full bg-[#2E7D32] text-white text-xs md:text-2xl py-2.5 md:py-6" data-id="element-175">
                → QUIERO EL PACK VIP ⚡
              </CTAButton>
            </div>
          </div>
          <div className="mt-6 text-center" data-id="element-176">
            <p className="text-red-400/80 text-[10px] md:text-xs font-medium" data-id="element-177">
              ⚠️ Después sube $5.000 ARS. No vuelve.
            </p>
          </div>
        </div>
      </section>

      {/* VIP Info Block */}
      <section className="py-6 md:py-12 bg-[#ECEFF1]" data-id="element-178">
        <div className="container mx-auto px-4" data-id="element-179">
          <div className="max-w-3xl mx-auto bg-white p-8 md:p-12 rounded-3xl border-4 border-[#2E7D32] shadow-xl" data-id="element-180">
            <h2 className="text-3xl font-bold mb-6 text-[#1A237E] text-center" data-id="element-181">
              💎 ¿POR QUÉ LLEVARTE EL PACK VIP?
            </h2>
            <div className="space-y-6 text-lg text-[#37474F] leading-relaxed" data-id="element-182">
              <p data-id="element-183">Vender más es solo la mitad del trabajo.</p>
              <p data-id="element-184">
                La otra mitad es saber exactamente cuánto te queda después de
                pagar todo.
              </p>
              <p data-id="element-185">
                El Pack VIP agrega el único módulo que convierte a un
                emprendedor que trabaja mucho en un dueño que crece con control.
              </p>
              <p className="font-bold text-[#1A237E]" data-id="element-186">
                Con Master-Finanzas vas a saber:
              </p>
              <ul className="space-y-3" data-id="element-187">
                <li className="flex items-center gap-3" data-id="element-188">
                  <CheckCircle2 size={20} className="text-[#2E7D32] shrink-0" data-id="element-189" />
                  <span data-id="element-190">Cuánto ganás realmente cada semana</span>
                </li>
                <li className="flex items-center gap-3" data-id="element-191">
                  <CheckCircle2 size={20} className="text-[#2E7D32] shrink-0" data-id="element-192" />
                  <span data-id="element-193">En qué se va la plata sin que lo notes</span>
                </li>
                <li className="flex items-center gap-3" data-id="element-194">
                  <CheckCircle2 size={20} className="text-[#2E7D32] shrink-0" data-id="element-195" />
                  <span data-id="element-196">Cuánto necesitás vender para no perder dinero</span>
                </li>
                <li className="flex items-center gap-3" data-id="element-197">
                  <CheckCircle2 size={20} className="text-[#2E7D32] shrink-0" data-id="element-198" />
                  <span data-id="element-199">Cuándo y cuánto reinvertir para seguir creciendo</span>
                </li>
              </ul>
              <p data-id="element-200">
                Porque un negocio visible que no controla sus números es un
                negocio que trabaja para pagar gastos.
              </p>
              <p data-id="element-201">
                Un negocio visible que SÍ controla sus números es un negocio que
                acumula, escala y crece.
              </p>
              <p className="font-bold text-[#2E7D32] text-center text-xl" data-id="element-202">
                Esa es la diferencia entre el Pack Completo y el Pack VIP.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-4 md:py-12 bg-white" data-id="element-203">
        <div className="container mx-auto px-4" data-id="element-204">
          <SectionTitle subtitle="El próximo cliente que perdas podría ser diferente." data-id="element-205">
            <span className="text-[#1A237E]" data-id="element-206">
              ⚖️ ¿CUÁNTO CUESTA NO TENERLO?
            </span>
          </SectionTitle>

          {/* Mobile View */}
          <div className="block md:hidden space-y-2 max-w-sm mx-auto" data-id="element-207">
            {[{
            s: 'No saber qué publicar',
            no: 'Días sin publicar nada',
            yes: 'Tomo 2: calendario y plantillas listas'
          }, {
            s: 'Gastar en anuncios sin resultado',
            no: 'Tirar plata sin sistema',
            yes: 'Tomo 3: Meta Ads desde $5 USD por día'
          }, {
            s: 'Consultas que no cierran',
            no: 'Perder ventas en WhatsApp',
            yes: 'Bono 3: scripts de cierre probados'
          }, {
            s: 'No saber si el negocio gana o pierde',
            no: 'Caos financiero mensual',
            yes: 'VIP: cierre semanal y mensual'
          }, {
            s: 'Depender de clientes de siempre',
            no: 'Estancamiento del negocio',
            yes: 'Sistema completo de atracción'
          }].map((row, i) => <div key={i} className="border border-slate-100 rounded-lg overflow-hidden shadow-sm" data-id="element-208">
                <div className="bg-[#F5F5F5] px-3 py-1 text-[10px] font-bold text-[#1A237E] uppercase" data-id="element-209">
                  {row.s}
                </div>
                <div className="grid grid-cols-2 text-[10px]" data-id="element-210">
                  <div className="bg-[#FFEBEE] p-2 text-[#C62828] flex items-center gap-1 italic border-r border-white" data-id="element-211">
                    <XCircle size={12} className="shrink-0" data-id="element-212" /> {row.no}
                  </div>
                  <div className="bg-[#E8F5E9] p-2 text-[#2E7D32] font-bold flex items-center gap-1" data-id="element-213">
                    <CheckCircle2 size={12} className="shrink-0" data-id="element-214" /> {row.yes}
                  </div>
                </div>
              </div>)}
            <div className="bg-[#1A237E] text-white p-3 text-center rounded-lg mt-4" data-id="element-215">
              <p className="text-xs font-bold" data-id="element-216">
                "Tu negocio puede tener clientes nuevos desde hoy. 🔥"
              </p>
            </div>
          </div>

          {/* Desktop View */}
          <div className="hidden md:block max-w-4xl mx-auto overflow-hidden rounded-2xl border border-slate-200 shadow-xl" data-id="element-217">
            <table className="w-full text-left border-collapse" data-id="element-218">
              <thead data-id="element-219">
                <tr className="bg-[#1A237E] text-white" data-id="element-220">
                  <th className="p-4 md:p-6 font-bold" data-id="element-221">SITUACIÓN</th>
                  <th className="p-4 md:p-6 font-bold" data-id="element-222">❌ SIN EL PACK</th>
                  <th className="p-4 md:p-6 font-bold" data-id="element-223">✅ CON EL PACK</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-224">
                {[{
                s: 'No saber qué publicar',
                no: 'Días sin publicar nada',
                yes: 'Tomo 2: calendario y plantillas listas'
              }, {
                s: 'Gastar en anuncios sin resultado',
                no: 'Tirar plata sin sistema',
                yes: 'Tomo 3: Meta Ads desde $5 USD por día'
              }, {
                s: 'Consultas que no cierran',
                no: 'Perder ventas en WhatsApp',
                yes: 'Bono 3: scripts de cierre probados'
              }, {
                s: 'No saber si el negocio gana o pierde',
                no: 'Caos financiero mensual',
                yes: 'VIP: cierre semanal y mensual'
              }, {
                s: 'Depender de clientes de siempre',
                no: 'Estancamiento del negocio',
                yes: 'Sistema completo de atracción'
              }].map((row, i) => <tr key={i} className={`${i % 2 === 0 ? 'bg-white' : 'bg-[#FAFAFA]'} hover:bg-slate-50 transition-colors`} data-id="element-225">
                    <td className="p-4 md:p-6 text-sm font-bold text-[#1A237E] bg-[#F5F5F5]" data-id="element-226">
                      {row.s}
                    </td>
                    <td className="p-4 md:p-6 text-sm text-[#C62828] bg-[#FFEBEE] italic flex items-center gap-2" data-id="element-227">
                      <XCircle size={16} className="shrink-0" data-id="element-228" /> {row.no}
                    </td>
                    <td className="p-4 md:p-6 text-sm font-bold text-[#2E7D32] bg-[#E8F5E9] flex items-center gap-2" data-id="element-229">
                      <CheckCircle2 size={16} className="shrink-0" data-id="element-230" /> {row.yes}
                    </td>
                  </tr>)}
              </tbody>
            </table>
            <div className="bg-[#1A237E] text-white p-8 text-center border-t-4 border-[#2E7D32]" data-id="element-231">
              <p className="text-xl font-bold" data-id="element-232">
                "Tu negocio puede tener clientes nuevos desde hoy. 🔥"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials 2 */}
      <section className="py-6 md:py-12 bg-[#ECEFF1]" data-id="element-233">
        <div className="container mx-auto px-4" data-id="element-234">
          <SectionTitle subtitle="Más profesionales que ya lo usan" data-id="element-235">
            <span className="text-[#1A237E]" data-id="element-236">❤️ TESTIMONIOS</span>
          </SectionTitle>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-237">
            {[{
            n: 'Javier M.',
            l: 'Mendoza · Plomero',
            q: 'El Tomo 3 lo apliqué el primer fin de semana. Con $5 USD por día tuve 12 consultas en una semana. Nunca había tenido eso.',
            i: 'JM'
          }, {
            n: 'Laura P.',
            l: 'Buenos Aires · Peluquera',
            q: 'El Bono 3 lo tengo abierto en el celular todo el día. Los scripts de WhatsApp me cambiaron la forma de cerrar ventas.',
            i: 'LP'
          }, {
            n: 'Diego G.',
            l: 'Córdoba · Carpintero',
            q: 'El Tomo 4 me hizo subir un video del proceso de mi trabajo. Llegó a 80.000 personas en 48 horas. El teléfono no paró de sonar.',
            i: 'DG'
          }, {
            n: 'Ricardo M.',
            l: 'Rosario · Dueño de taller mecánico',
            q: 'El módulo VIP me hizo darme cuenta que tenía $35.000 por mes en gastos que ni sabía que tenía. Eso solo pagó el pack 10 veces.',
            i: 'RM'
          }].map((test, i) => <div key={i} className="bg-white p-8 rounded-2xl shadow-sm border-l-8 border-[#1A237E] flex gap-6 items-start" data-id="element-238">
                <div className="w-12 h-12 bg-[#2E7D32] rounded-full shrink-0 flex items-center justify-center font-bold text-white text-lg" data-id="element-239">
                  {test.i}
                </div>
                <div data-id="element-240">
                  <div className="flex gap-1 mb-2 text-yellow-500" data-id="element-241">
                    {[...Array(5)].map((_, j) => <Star key={j} size={14} fill="currentColor" data-id="element-242" />)}
                  </div>
                  <p className="text-[#37474F] italic mb-4" data-id="element-243">"{test.q}"</p>
                  <p className="font-bold text-[#1A237E] text-sm" data-id="element-244">{test.n}</p>
                  <p className="text-xs text-[#37474F]" data-id="element-245">{test.l}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="py-6 md:py-12 bg-[#E8F5E9]" data-id="element-246">
        <div className="container mx-auto px-4 text-center" data-id="element-247">
          <div className="max-w-3xl mx-auto bg-white p-8 md:p-12 rounded-3xl border-2 border-[#2E7D32] shadow-sm" data-id="element-248">
            <ShieldCheck size={64} className="text-[#2E7D32] mx-auto mb-6" data-id="element-249" />
            <h2 className="text-3xl font-bold mb-4 text-[#2E7D32]" data-id="element-250">
              GARANTÍA 7 DÍAS SIN PREGUNTAS
            </h2>
            <p className="text-lg text-[#37474F] mb-8 leading-relaxed" data-id="element-251">
              Si abrís el pack y no es lo que esperabas: <br data-id="element-252" />
              <span className="text-3xl font-bold text-[#2E7D32] block mt-4" data-id="element-253">
                DEVOLUCIÓN DEL 100%
              </span>
              <span className="text-sm text-[#37474F] mt-2 block" data-id="element-254">
                sin formularios ni vueltas.
              </span>
            </p>
            <p className="text-xs text-[#37474F] uppercase tracking-widest font-bold" data-id="element-255">
              Tasa de devoluciones: menos del 1%
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-6 md:py-12 bg-white" data-id="element-256">
        <div className="container mx-auto px-4" data-id="element-257">
          <SectionTitle subtitle="Despejá tus dudas y empezá hoy mismo." data-id="element-258">
            <span className="text-[#1A237E]" data-id="element-259">❓ LO QUE MÁS NOS PREGUNTAN</span>
          </SectionTitle>
          <div className="max-w-3xl mx-auto space-y-4" data-id="element-260">
            {[{
            q: '¿Para quién es este pack?',
            a: 'Para dueños de negocios físicos, emprendedores y prestadores de servicios en Argentina que quieren conseguir más clientes usando redes sociales, Meta Ads y WhatsApp. No hace falta saber nada de marketing. Todo está explicado desde cero.'
          }, {
            q: '¿Cómo recibo el material?',
            a: '100% digital en formato PDF. Acceso inmediato después del pago. Te llega por email en menos de 5 minutos. Podés verlo desde el celular, tablet o computadora.'
          }, {
            q: '¿Qué lo diferencia de otros cursos?',
            a: 'No es un curso con videos que nunca terminás de ver. Es un sistema completo de materiales listos para aplicar hoy mismo. Con ejemplos reales de negocios argentinos, en lenguaje argentino y adaptado a la realidad del mercado local.'
          }, {
            q: '¿Tiene garantía real?',
            a: 'Sí. 7 días de garantía total. Si no es lo que esperabas, te devolvemos el 100% del dinero sin preguntas y sin formularios.'
          }, {
            q: '¿Sirve para mi rubro específico?',
            a: 'Sí. El sistema está diseñado para cualquier negocio físico o de servicios: barberos, peluquerías, electricistas, plomeros, talleres, pizzerías, esteticistas, fotógrafos, entrenadores, contadores, abogados y cualquier comercio de barrio.'
          }].map((faq, i) => <details key={i} className="group bg-[#E8EAF6] rounded-xl overflow-hidden" data-id="element-261">
                <summary className="flex items-center justify-between p-6 cursor-pointer font-bold text-[#1A237E] list-none" data-id="element-262">
                  {faq.q}
                  <ChevronDown size={20} className="text-[#2E7D32] group-open:rotate-180 transition-transform" data-id="element-263" />
                </summary>
                <div className="px-6 pb-6 bg-white text-[#37474F] leading-relaxed text-sm border-l-8 border-[#2E7D32]" data-id="element-264">
                  {faq.a}
                </div>
              </details>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="final-cta" className="py-6 md:py-14 bg-[#1A237E] text-white text-center" data-id="element-265">
        <div className="container mx-auto px-4" data-id="element-266">
          <div className="mb-10" data-id="element-267">
            <SessionCountdownTimer data-id="element-268" />
          </div>

          <div className="max-w-2xl mx-auto mb-8" data-id="element-269">
            <div className="bg-[#0D1B2A] rounded-3xl p-8 md:p-12 text-white shadow-2xl flex flex-col border-4 border-[#2E7D32] relative" data-id="element-270">
              <h3 className="text-2xl font-bold mb-2" data-id="element-271">
                MEGA PACK NEGOCIO VISIBLE
              </h3>
              <p className="text-white/70 mb-6" data-id="element-272">
                Todo lo que necesitas para atraer clientes y vender más desde tu
                celular.
              </p>
              <p className="text-[#9E9E9E] line-through text-lg" data-id="element-273">$48.000</p>
              <p className="text-6xl font-bold text-[#2E7D32] mb-6" data-id="element-274">
                $19.990 ARS
              </p>
              <div className="mb-8 text-[11px] md:text-sm text-white/90 text-center space-y-1 font-medium" data-id="element-275">
                <p data-id="element-276">✅ 5 Tomos (350+ páginas)</p>
                <p data-id="element-277">✅ 3 Bonos ($24.000 valor)</p>
                <p data-id="element-278">✅ Acceso inmediato</p>
                <p data-id="element-279">✅ Garantía 7 días</p>
              </div>
              <CTAButton href="https://checkout.guiadigital360.online/cart/44902679969861:1" className="w-full bg-[#2E7D32] text-white text-2xl py-6" data-id="element-280">
                → QUIERO ESTE PACK ⚡
              </CTAButton>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-4 md:gap-8" data-id="element-281">
            <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-[#1A237E] font-bold text-xs uppercase tracking-widest" data-id="element-282">
              <ShieldCheck size={16} className="text-[#2E7D32]" data-id="element-283" /> Pago 100%
              Seguro
            </div>
            <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-[#1A237E] font-bold text-xs uppercase tracking-widest" data-id="element-284">
              <CheckCircle2 size={16} className="text-[#2E7D32]" data-id="element-285" /> Garantía de
              Satisfacción
            </div>
            <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-[#1A237E] font-bold text-xs uppercase tracking-widest" data-id="element-286">
              <Zap size={16} className="text-[#2E7D32]" data-id="element-287" /> Acceso Instantáneo
            </div>
          </div>

          <div className="mt-8 bg-[#C62828] text-white p-3 rounded-lg inline-block font-bold" data-id="element-288">
            ⚠️ Después sube $5.000 ARS. No vuelve.
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 bg-[#0D1B2A] text-white/60 border-t border-white/5" data-id="element-289">
        <div className="container mx-auto px-4 text-center" data-id="element-290">
          <p className="font-bold text-white text-2xl mb-3 font-display" data-id="element-291">
            🔥 MEGA PACK NEGOCIO VISIBLE™
          </p>
          <p className="text-sm mb-6 max-w-2xl mx-auto leading-relaxed text-white/70" data-id="element-292">
            El sistema de marketing digital N°1 para negocios locales,
            emprendedores y prestadores de servicios que necesitan más clientes,
            más ventas y más control de su negocio en Argentina.
          </p>
          <div className="flex flex-col md:flex-row justify-center gap-6 md:gap-12 mb-8 text-sm" data-id="element-293">
            <a href="mailto:soporteguiadigital360@gmail.com" className="text-[#2E7D32] hover:text-white transition-colors flex items-center justify-center gap-2" data-id="element-294">
              <MessageSquare size={16} data-id="element-295" /> soporteguiadigital360@gmail.com
            </a>
            <div className="flex items-center justify-center gap-2 text-[#2E7D32]" data-id="element-296">
              <Star size={16} data-id="element-297" /> @guiadigital360
            </div>
          </div>
          <div className="w-full h-px bg-white/5 mb-6" data-id="element-298"></div>
          <p className="text-xs text-[#B0BEC5]" data-id="element-299">
            © GuíaDigital360 2026 — Todos los derechos reservados.
          </p>
          <p className="text-white mt-4 flex items-center justify-center gap-2 font-bold" data-id="element-300">
            "El que más se ve, más vende. Empezá hoy. 🔥"
          </p>
        </div>
      </footer>
    </div>;
}